import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Lock, BookOpen, CircleCheck, CircleCheckBig, Link2Off, TriangleAlert, WifiOff } from 'lucide-react';
import { acceptInvitation } from '../../api/teachers';
import { storeToken, getTokenFromStorage } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { useI18n } from '../../i18n/I18nContext';
import AuthLayout from '../Login/AuthLayout';
import AuthField from '../Login/AuthField';
import AuthSubmit from '../Login/AuthSubmit';
import { canAutoFocus, focusField } from '../Login/authHelpers';

const SUPPORT_EMAIL = 'support@darsly.uz';
const PASSWORD_RULE = /^(?=.*[A-Za-z])(?=.*\d).{8,}$/;
/** The icon and tone for each problem: the link is no good, the network is down, something broke. */
const PROBLEM_ART = {
  invalid: [Link2Off, 'warning'],
  expired: [Link2Off, 'warning'],
  used: [TriangleAlert, 'danger'],
  network: [WifiOff, 'neutral'],
  generic: [TriangleAlert, 'danger'],
};

function getErrorState(err, t) {
  const state = (kind, title, body, actionLabel, action) => ({ kind, title, body, actionLabel, action });

  if (!err) {
    return state('invalid', t('teacherAdmin.accept.invalidTitle'), t('teacherAdmin.accept.invalidBody'),
      t('teacherAdmin.accept.backToLogin'), 'login');
  }

  const message = String(err.message || '').toLowerCase();
  const hasSession = !!getTokenFromStorage('accessToken');

  if (err.code === 409 || message.includes('already')) {
    return state('used', t('teacherAdmin.accept.alreadyAcceptedTitle'), t('teacherAdmin.accept.alreadyAcceptedBody'),
      hasSession ? t('teacherAdmin.accept.goToDashboard') : t('teacherAdmin.accept.backToLogin'),
      hasSession ? 'dashboard' : 'login');
  }

  if (err.code === 401 || message.includes('unauthorized') || message.includes('revoked')) {
    return state('expired', t('teacherAdmin.accept.expiredTitle'), t('teacherAdmin.accept.expiredBody'),
      t('teacherAdmin.accept.backToLogin'), 'login');
  }

  // The backend's own wording is English, so a 422 gets the page's sentence, never err.message.
  if (err.code === 422) {
    return state('invalid', t('teacherAdmin.accept.invalidTitle'), t('teacherAdmin.accept.invalidBody'),
      t('teacherAdmin.accept.backToLogin'), 'login');
  }

  if (err.code === 400 || message.includes('expired') || message.includes('invalid')) {
    return state('expired', t('teacherAdmin.accept.expiredTitle'), t('teacherAdmin.accept.expiredBody'),
      t('teacherAdmin.accept.backToLogin'), 'login');
  }

  if (!err.code || message.includes('network') || message.includes('timeout')) {
    return state('network', t('teacherAdmin.accept.networkTitle'), t('teacherAdmin.accept.networkBody'),
      t('teacherAdmin.accept.retry'), 'retry');
  }

  return state('generic', t('teacherAdmin.accept.genericTitle'), t('teacherAdmin.accept.genericBody'),
    t('teacherAdmin.accept.backToLogin'), 'login');
}

function AcceptInvitation() {
  const { t } = useI18n();
  const { token: pathToken } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { restoreSession } = useAuth();
  const token = searchParams.get('token') || pathToken || '';

  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [errorState, setErrorState] = useState(null);
  const [successState, setSuccessState] = useState(null);

  // A link without a token can never work, so the problem page shows at once (no form flash).
  const problem = errorState || (token ? null : getErrorState(null, t));

  useEffect(() => {
    if (!successState) {
      return undefined;
    }

    const redirectTimer = window.setTimeout(() => {
      navigate('/teacher/dashboard', { replace: true });
    }, 1500);

    return () => window.clearTimeout(redirectTimer);
  }, [navigate, successState]);

  const getSuccessBody = () => {
    if (!successState?.courseName) {
      return t('teacherAdmin.accept.successFallbackBody');
    }

    return t('teacherAdmin.accept.successWithCourseBody', { course: successState.courseName });
  };

  const validate = () => {
    const errors = {};
    if (!name.trim()) {
      errors.name = t('teacherAdmin.accept.errors.nameRequired');
    }
    if (!password) {
      errors.password = t('teacherAdmin.accept.errors.passwordRequired');
    } else if (!PASSWORD_RULE.test(password)) {
      errors.password = t('teacherAdmin.accept.errors.passwordWeak');
    }
    if (password && confirmPassword !== password) {
      errors.confirmPassword = t('teacherAdmin.accept.errors.passwordMismatch');
    }
    setFieldErrors(errors);
    // the caret goes to the first field that needs attention
    const first = ['name', 'password', 'confirmPassword'].find((field) => errors[field]);
    if (first) focusField(first);
    return !first;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorState(null);

    if (!validate()) return;

    setLoading(true);

    try {
      const response = await acceptInvitation({
        token,
        name: name.trim(),
        password,
      });

      if (response?.accessToken) {
        storeToken('accessToken', response.accessToken);
        if (response.refreshToken) storeToken('refreshToken', response.refreshToken);
        await restoreSession();
        setSuccessState({
          courseName: response.courseName || null,
        });
      } else {
        // An answer without a session is not a success: say so instead of doing nothing.
        setErrorState(getErrorState({ code: 500 }, t));
      }
    } catch (err) {
      setErrorState(getErrorState(err, t));
    } finally {
      setLoading(false);
    }
  };

  const clearError = (field) => setFieldErrors((current) => ({ ...current, [field]: undefined }));

  const steps = [
    { icon: <Lock size={18} />, title: t('teacherAdmin.accept.step1Title'), desc: t('teacherAdmin.accept.step1Desc') },
    { icon: <BookOpen size={18} />, title: t('teacherAdmin.accept.step2Title'), desc: t('teacherAdmin.accept.step2Desc') },
    { icon: <CircleCheck size={18} />, title: t('teacherAdmin.accept.step3Title'), desc: t('teacherAdmin.accept.step3Desc') },
  ];

  const layout = {
    badge: t('teacherAdmin.accept.badge'),
    tagline: t('teacherAdmin.accept.tagline1'),
    taglineEm: t('teacherAdmin.accept.taglineEm'),
    desc: t('teacherAdmin.accept.desc'),
    features: steps,
  };

  if (problem) {
    const [ProblemIcon, problemTone] = PROBLEM_ART[problem.kind] || PROBLEM_ART.generic;
    return (
      <AuthLayout {...layout} title={problem.title}>
        <div className="auth-state">
          <div className="auth-state-art" data-tone={problemTone}>
            <ProblemIcon aria-hidden="true" />
          </div>
          <div className="auth-form-header">
            <h1>{problem.title}</h1>
            <p>{problem.body}</p>
          </div>
          <p className="auth-note">{t('teacherAdmin.accept.support', { email: SUPPORT_EMAIL })}</p>
          <button
            type="button"
            onClick={() => {
              if (problem.action === 'retry') {
                setErrorState(null);
                return;
              }
              if (problem.action === 'dashboard') {
                navigate('/teacher/dashboard');
                return;
              }
              navigate('/teacher-login');
            }}
            className="auth-submit"
          >
            {problem.actionLabel}
          </button>
        </div>
      </AuthLayout>
    );
  }

  if (successState) {
    return (
      <AuthLayout {...layout} title={t('teacherAdmin.accept.successTitle')}>
        <div className="auth-state">
          <div className="auth-state-art" data-tone="success">
            <CircleCheckBig aria-hidden="true" />
          </div>
          <div className="auth-form-header">
            <h1>{t('teacherAdmin.accept.successTitle')}</h1>
            <p>{getSuccessBody()}</p>
          </div>
          <div className="auth-success" role="status">{t('teacherAdmin.accept.successCountdown')}</div>
          <button
            type="button"
            onClick={() => navigate('/teacher/dashboard', { replace: true })}
            className="auth-submit"
          >
            {t('teacherAdmin.accept.successButton')}
          </button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      {...layout}
      title={t('teacherAdmin.accept.pageTitle')}
      below={<Link to="/teacher-login">{t('teacherAdmin.accept.backToLogin')}</Link>}
    >
      <div className="auth-form-header">
        <h1>{t('teacherAdmin.accept.pageTitle')}</h1>
        <p>{t('teacherAdmin.accept.subtitle')}</p>
      </div>

      <form onSubmit={handleSubmit} noValidate aria-busy={loading}>
        <AuthField
          id="name"
          label={t('teacherAdmin.accept.fullName')}
          value={name}
          onChange={(e) => { setName(e.target.value); clearError('name'); }}
          placeholder={t('teacherAdmin.accept.fullNamePlaceholder')}
          error={fieldErrors.name}
          readOnly={loading}
          autoComplete="name"
          autoFocus={canAutoFocus()}
        />

        <AuthField
          id="password"
          type="password"
          label={t('teacherAdmin.accept.password')}
          value={password}
          onChange={(e) => { setPassword(e.target.value); clearError('password'); }}
          placeholder={t('teacherAdmin.accept.passwordPlaceholder')}
          error={fieldErrors.password}
          hint={t('teacherAdmin.accept.passwordHint')}
          readOnly={loading}
          autoComplete="new-password"
        />

        <AuthField
          id="confirmPassword"
          type="password"
          label={t('teacherAdmin.accept.confirmPassword')}
          value={confirmPassword}
          onChange={(e) => { setConfirmPassword(e.target.value); clearError('confirmPassword'); }}
          placeholder={t('teacherAdmin.accept.confirmPasswordPlaceholder')}
          error={fieldErrors.confirmPassword}
          readOnly={loading}
          autoComplete="new-password"
        />

        <AuthSubmit
          busy={loading}
          label={t('teacherAdmin.accept.submit')}
          busyLabel={t('teacherAdmin.accept.submitting')}
        />
      </form>

      <div className="auth-info-box auth-info-box--after">
        {t('teacherAdmin.accept.agreement')}
      </div>
    </AuthLayout>
  );
}

export default AcceptInvitation;
